import {FC, useState, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import styled, {keyframes} from 'styled-components'

import {CheckIcon, CopyIcon, RenderIf} from 'components'

interface AnimatedCopyProps {
  text: string
  dataCy: string
  copyTimeout?: number
  ariaLabel?: string
}

// Define keyframes for the animations
const shrink = keyframes`
  0% {
    stroke-width: 1.2;
    stroke: white;
  }
  100% {
    stroke-width: 1;
    stroke: gray;
  }
`

const moveDown = keyframes`
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(1.2px, 1.2px);
  }
`

const grow = keyframes`
  0% {
    stroke-width: 1;
    stroke: gray;
  }
  100% {
    stroke-width: 1.2;
    stroke: white;
  }
`

const moveUp = keyframes`
  0% {
    transform: translate(1.2px, 1.2px);
  }
  100% {
    transform: translate(0, 0);
  }
`

const draw = keyframes`
  0% {
    stroke-dashoffset: 100;
  }
  100% {
    stroke-dashoffset: 0;
  }
`

const CopyWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  vertical-align: middle;

  &:hover {
    .copy-icon-svg path:first-child {
      animation: ${shrink} 0.15s forwards;
    }

    .copy-icon-svg path:last-child {
      animation: ${moveDown} 0.15s forwards;
    }
  }

  &:not(:hover) {
    .copy-icon-svg path:first-child {
      animation: ${grow} 0.3s forwards;
    }

    .copy-icon-svg path:last-child {
      animation: ${moveUp} 0.3s forwards;
    }
  }

  // animate check mark appear and disappear
  .check-mark-wrapper {
    svg path {
      stroke-dasharray: 100;
      stroke-dashoffset: 100;
      animation: ${draw} 2s linear forwards;
    }
  }
`

export const AnimatedCopy: FC<AnimatedCopyProps> = ({
  text,
  dataCy,
  copyTimeout = 2000,
  ariaLabel,
}) => {
  const [isCheckMark, setIsCheckMark] = useState(false)
  const {t} = useTranslation(['common'])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setIsCheckMark(true)

      // Clear any existing timeout before setting a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setIsCheckMark(false)
        timeoutRef.current = null
      }, copyTimeout)
    } catch (error) {
      console.error('Failed to copy text to clipboard:', error)
      // Optionally show a toast or error message to the user
    }
  }

  return (
    <CopyWrapper>
      <RenderIf isTrue={isCheckMark}>
        <span
          className="relative check-mark-wrapper flex size-6 shrink-0 items-center justify-center"
          role="status"
          aria-live="polite"
        >
          <span
            className="bg-gray-1400 text-xs h-6 w-17 flex justify-center items-center rounded karla mx-2 absolute right-5 text-white"
            data-cy="copied-text"
          >
            {t('COPIED')}
          </span>
          <CheckIcon classes="w-6 h-6 stroke-green-100" />
        </span>
      </RenderIf>
      <RenderIf isTrue={!isCheckMark}>
        <button
          type="button"
          onClick={handleClick}
          data-cy={dataCy}
          aria-label={ariaLabel ?? t('COPY')}
          className="m-0 inline-flex size-6 shrink-0 cursor-pointer items-center justify-center border-none bg-none p-0 leading-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dope-cyan-bright"
        >
          <CopyIcon width={24} height={24} classes="block" />
        </button>
      </RenderIf>
    </CopyWrapper>
  )
}

export default AnimatedCopy
